// src/components/ConnectionRequestButton.jsx
import React, { useMemo, useState } from 'react';
import Modal from '@/components/Modal';
import { toast } from 'react-hot-toast';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '@root/firebase';

export default function ConnectionRequestButton({
  senderId, // current user's uid
  senderName, // current user's display name
  senderAvatar, // current user's avatar url
  receiverId, // target user's uid
  isFriend = false, // already connected?
  isPending = false, // already sent & pending?
  onPending, // optional callback when pending created
  className = 'reaction-btn reaction-action',
  tooltipLabel = 'Connect?',
  size = 'medium', // 'small' | 'medium' (affects Modal size only)
}) {
  const [submitting, setSubmitting] = useState(false);
  const modalId = useMemo(
    () => `confirmConnection-${receiverId}`,
    [receiverId]
  );

  const disabled = isFriend || isPending || submitting;
  const iconClass = `mdi ${
    isFriend
      ? 'mdi-account-check'
      : isPending
      ? 'mdi-account-clock-outline'
      : 'mdi-account-plus-outline'
  }`;

  async function handleConfirm() {
    if (!senderId || !receiverId) return;
    if (isFriend) {
      toast.success("You're already connected.");
      return;
    }
    if (isPending) {
      toast('Request already sent.');
      return;
    }

    setSubmitting(true);
    try {
      const notifRef = doc(db, `users/${receiverId}/notifications/${senderId}`);

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(notifRef);
        if (snap.exists()) throw new Error('already_exists');
        tx.set(notifRef, {
          type: 'connection_request',
          senderId,
          receiverId,
          status: 'pending',
          createdAt: serverTimestamp(),
          readAt: null,
          senderName: senderName || '',
          senderAvatar: senderAvatar || '',
        });
      });

      onPending?.(receiverId);
      toast.success('Connection request sent!');
    } catch (e) {
      if (e?.message === 'already_exists') {
        onPending?.(receiverId);
        toast('Request already pending.');
      } else {
        console.error('connect request error', e);
        toast.error('Could not send request.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className='tooltip-container'>
        <button
          type='button'
          className={className}
          data-bs-toggle='modal'
          data-bs-target={`#${modalId}`}
          disabled={disabled}
          title={tooltipLabel}
        >
          <i className={iconClass} />
        </button>
        <span className='tooltip'>
          {isFriend ? 'Unconnect?' : isPending ? 'Pending' : 'Connect?'}
        </span>
      </div>

      {/* Confirm modal */}
      <Modal
        id={modalId}
        title='Confirm Friend Request'
        onConfirm={handleConfirm}
        confirmText={submitting ? 'Sending…' : 'Yes'}
        loading={submitting}
        loadingText='Sending…'
        size={size}
      >
        <p>Are you sure you want to send a friend request?</p>
      </Modal>
    </>
  );
}
